import React, { useCallback, useEffect, useState } from 'react';
import Icon from '../AppIcon';
import StatusPill from '../orizon-ui/StatusPill';
import { FormSection } from '../orizon-ui/FormField';
import { useToast } from '../orizon-ui/Toast';
import { useConfirm } from '../orizon-ui/ConfirmDialog';
import { formattaQta, formattaData } from '../gru/scorta';
import {
  colataLista, colataSegnaOrdineRicevuto, mapColataError,
} from '../../lib/services/colataService';
import { useColataPermessi } from './ColataPermessi';
import { CaricoModal } from './AzioniGiacenza';

const Bottone = ({ icona, label, onClick, disabilitato, tono = 'neutro' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabilitato}
    className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 ${
      tono === 'verde'
        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/20'
        : 'bg-muted/30 border-border/40 text-muted-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary'
    }`}
  >
    <Icon name={icona} size={13} />
    {label}
  </button>
);

/**
 * Ordini aperti di un articolo, con le due azioni che chiudono un ordine:
 *  - "Registra ricezione": apre il carico collegato all'ordine (quantità a scelta, anche parziale);
 *  - "Segna come ricevuto": carica il residuo e chiude l'ordine.
 * In entrambi i casi la giacenza aumenta e lo Storico riceve il movimento "Carico" collegato all'ordine.
 * Dopo ogni azione l'elenco si ricarica e viene chiamato onCambiato (il chiamante aggiorna articolo e liste).
 * Non mostra nulla se non ci sono ordini aperti. In "sola lettura" mostra l'elenco senza pulsanti.
 *
 * onModaleChange(aperto): avvisa quando si apre/chiude il modale del carico.
 * disabilitato / motivoDisabilitato: blocca i pulsanti (es. la forma di modifica ha modifiche non salvate).
 */
const OrdiniAperti = ({ articolo, onCambiato, onModaleChange, disabilitato = false, motivoDisabilitato = '' }) => {
  const { scrivere } = useColataPermessi();
  const toast = useToast();
  const confirm = useConfirm();
  const [ordini, setOrdini] = useState([]);
  const [occupato, setOccupato] = useState(false);
  const [ricezioneDi, setRicezioneDi] = useState(null); // ordine per cui è aperto il carico

  const dec = Number(articolo.unita_decimali ?? 0);

  const carica = useCallback(async () => {
    try {
      const r = await colataLista('interventi', {
        perPagina: 50,
        soloAttivi: false,
        sort: { key: 'data_intervento', direction: 'desc' },
        filtri: { articolo_id: articolo.id, stato: 'ordinato' },
      });
      setOrdini(r.righe);
    } catch (e) {
      toast.error(mapColataError(e).message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articolo.id]);

  useEffect(() => {
    carica();
  }, [carica]);

  // il chiamante può bloccare la chiusura del proprio modale mentre è aperto il carico
  useEffect(() => {
    onModaleChange?.(Boolean(ricezioneDi));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ricezioneDi]);

  const dopoAzione = async () => {
    setRicezioneDi(null);
    await carica();
    onCambiato?.();
  };

  const segnaRicevuto = async (o) => {
    const residuo = Number(o.qta_residua ?? o.qta);
    const ok = await confirm({
      titolo: 'Segnare l\'ordine come ricevuto?',
      descrizione: `Verranno caricati ${formattaQta(residuo, dec)} ${articolo.unita_label} di "${articolo.nome}" in magazzino e l'ordine verrà chiuso. Il movimento comparirà nello Storico.`,
      confermaLabel: 'Segna come ricevuto',
      variante: 'primary',
    });
    if (!ok) return;

    setOccupato(true);
    try {
      const r = await colataSegnaOrdineRicevuto({ interventoId: o.id });
      toast.success(`Ordine ricevuto: caricati ${formattaQta(r.qta_caricata ?? residuo, dec)} ${articolo.unita_label}, giacenza ${formattaQta(r.qta, dec)}.`);
      await dopoAzione();
    } catch (e) {
      toast.error(mapColataError(e).message);
    } finally {
      setOccupato(false);
    }
  };

  if (ordini.length === 0) return null;

  const bloccato = disabilitato || occupato;

  return (
    <FormSection titolo="Ordini aperti" icona="ClipboardList">
      <div className="flex flex-col gap-2">
        {ordini.map((o) => (
          <div key={o.id} className="flex flex-col gap-3 px-4 py-3 rounded-2xl bg-muted/20 border border-border/30">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3 min-w-0">
                <StatusPill tono="warn" label="Ordinato" />
                <span className="text-[13px] font-black tabular-nums text-foreground/80">
                  residuo {formattaQta(o.qta_residua ?? o.qta, dec)} {articolo.unita_label}
                  <span className="ml-1.5 text-[10px] font-bold text-muted-foreground/60">
                    (ordinati {formattaQta(o.qta, dec)}, ricevuti {formattaQta(o.qta_ricevuta ?? 0, dec)})
                  </span>
                </span>
                <span className="text-[11px] font-bold text-muted-foreground">
                  {o.zona_codice}{o.impianto_nome ? ` · ${o.impianto_nome}` : ''} · {formattaData(o.data_ordine || o.data_intervento)}
                </span>
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50">
                {o.data_consegna_prevista ? `Consegna prevista ${formattaData(o.data_consegna_prevista)}` : 'Consegna non indicata'}
              </span>
            </div>

            {scrivere && (
              <div className="flex flex-wrap items-center gap-2">
                <Bottone icona="PackagePlus" label="Registra ricezione" onClick={() => setRicezioneDi(o)} disabilitato={bloccato} />
                <Bottone icona="CheckCheck" label="Segna come ricevuto" tono="verde" onClick={() => segnaRicevuto(o)} disabilitato={bloccato} />
              </div>
            )}
          </div>
        ))}
        {scrivere && disabilitato && motivoDisabilitato && (
          <p className="px-1 text-[10px] font-bold text-amber-500">{motivoDisabilitato}</p>
        )}
      </div>

      {ricezioneDi && (
        <CaricoModal
          articolo={articolo}
          ordineId={ricezioneDi.id}
          onClose={() => setRicezioneDi(null)}
          onDone={dopoAzione}
        />
      )}
    </FormSection>
  );
};

export default OrdiniAperti;
